import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { http } from "../api";
import { FileText, CheckCircle2, Star, Lock, Clock } from "lucide-react";

const draftKey = (slug) => `ward7crm.pf-draft.${slug}`;

export default function PublicForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // null | { thank_you_message, redirect_url }
  const [err, setErr] = useState("");
  const [closed, setClosed] = useState(false);
  const [page, setPage] = useState(1);
  // Save & Resume Later — same-device draft in localStorage
  const [resumeCandidate, setResumeCandidate] = useState(null); // { name, email, values, page, saved_at }
  const [draftStatus, setDraftStatus] = useState(""); // "Saved just now" | ""
  const draftStatusTimer = useRef(null);

  useEffect(() => {
    http.get(`/forms/public/${slug}`)
      .then((r) => {
        setForm(r.data);
        // After the form loads, check for a saved draft on this device.
        try {
          const raw = localStorage.getItem(draftKey(slug));
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && (parsed.values || parsed.name)) setResumeCandidate(parsed);
          }
        } catch { /* noop */ }
      })
      .catch((e) => {
        const detail = e.response?.data?.detail || "";
        if (e.response?.status === 404) { setClosed(true); setErr("This form is not accepting responses anymore."); }
        else setErr(detail || "Form not found or closed");
      });
  }, [slug]);

  const setVal = (fid, v) => setValues((cur) => ({ ...cur, [fid]: v }));

  // Auto-save the draft to localStorage every 4s while there is something to save.
  // Purges when the resume banner is still showing (waiting for user's decision) or after successful submit.
  useEffect(() => {
    if (!form || done || closed || resumeCandidate) return undefined;
    const iv = setInterval(() => {
      try {
        const hasName = !!name && name.trim();
        const hasValues = Object.values(values).some((v) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0));
        if (!hasName && !hasValues) return;
        localStorage.setItem(draftKey(slug), JSON.stringify({
          name, email, values, page, saved_at: new Date().toISOString(),
        }));
        setDraftStatus("Saved just now");
        if (draftStatusTimer.current) clearTimeout(draftStatusTimer.current);
        draftStatusTimer.current = setTimeout(() => setDraftStatus(""), 2500);
      } catch { /* noop */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [form, done, closed, resumeCandidate, slug, name, email, values, page]);

  const resumeDraft = () => {
    if (!resumeCandidate) return;
    setName(resumeCandidate.name || "");
    setEmail(resumeCandidate.email || "");
    setValues(resumeCandidate.values || {});
    setPage(Math.max(1, Number(resumeCandidate.page) || 1));
    setResumeCandidate(null);
  };
  const discardDraft = () => {
    try { localStorage.removeItem(draftKey(slug)); } catch { /* noop */ }
    setResumeCandidate(null);
  };
  const purgeDraft = () => {
    try { localStorage.removeItem(draftKey(slug)); } catch { /* noop */ }
    setDraftStatus("");
  };

  // ----- multi-page + conditional visibility helpers -----
  const isVisible = (f) => {
    const cond = f.visible_if;
    if (!cond || !cond.field_id || cond.value === undefined || cond.value === "") return true;
    const refVal = values[cond.field_id];
    if (Array.isArray(refVal)) return refVal.map(String).includes(String(cond.value));
    // Boolean triggers: value stored as bool; compare against "true"/"false" strings
    if (typeof refVal === "boolean") return String(refVal) === String(cond.value);
    return String(refVal ?? "") === String(cond.value);
  };

  const fields = form?.fields || [];
  const totalPages = Math.max(1, ...fields.map((f) => f.page || 1));
  const currentPageFields = fields.filter((f) => (f.page || 1) === page && isVisible(f));
  const isLastPage = page >= totalPages;

  const missingRequiredOnPage = () => {
    for (const f of currentPageFields) {
      if (!f.required) continue;
      const v = values[f.field_id];
      const empty = v === undefined || v === null || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && v.length === 0);
      if (empty) return f.label;
    }
    if (page === 1 && !name.trim()) return "Your name";
    return null;
  };

  const goNext = () => {
    setErr("");
    const missing = missingRequiredOnPage();
    if (missing) { setErr(`Please fill '${missing}' to continue.`); return; }
    setPage((p) => Math.min(totalPages, p + 1));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => { setErr(""); setPage((p) => Math.max(1, p - 1)); };

  const submit = async () => {
    setErr("");
    const missing = missingRequiredOnPage();
    if (missing) { setErr(`Please fill '${missing}' to continue.`); return; }
    // Strip values for currently-hidden fields so we don't send them.
    const clean = {};
    for (const f of fields) if (isVisible(f) && values[f.field_id] !== undefined) clean[f.field_id] = values[f.field_id];
    setBusy(true);
    try {
      const r = await http.post(`/forms/public/${slug}/submit`, { values: clean, submitter_name: name, submitter_email: email });
      const info = r.data || {};
      purgeDraft();
      if (info.redirect_url) { window.location.href = info.redirect_url; return; }
      setDone({ thank_you_message: info.thank_you_message || "" });
    } catch (e) {
      const st = e.response?.status;
      const msg = e.response?.data?.detail || "Submission failed";
      if (st === 410) { setClosed(true); setErr(msg); }
      else setErr(msg);
    } finally { setBusy(false); }
  };

  if (closed) return <PublicShell><div style={{ textAlign: "center", padding: 40 }}><Lock size={44} color="#b04a3f" style={{ display: "block", margin: "0 auto" }} /><h2 style={{ marginTop: 12 }}>Form closed</h2><p>{err || "This form is not accepting responses anymore."}</p></div></PublicShell>;
  if (err && !form) return <PublicShell><h2 style={{ color: "#b04a3f" }}>{err}</h2></PublicShell>;
  if (!form) return <PublicShell><p>Loading…</p></PublicShell>;
  if (form.requires_login) return <PublicShell><h2>Login required</h2><p>This form is not open to public submissions. Sign in to continue.</p></PublicShell>;
  if (done) return (
    <PublicShell>
      <div style={{ textAlign: "center", padding: 40 }} data-testid="pf-done">
        <CheckCircle2 size={48} color="#0f766e" style={{ display: "block", margin: "0 auto" }} />
        <h2 style={{ marginTop: 12 }}>Thank you!</h2>
        <p style={{ color: "#5f7568", whiteSpace: "pre-line" }}>{done.thank_you_message || "Your submission has been recorded."}</p>
      </div>
    </PublicShell>
  );

  return (
    <PublicShell>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
        <FileText size={22} color="#0f766e" />
        <h1 style={{ margin: 0 }}>{form.title}</h1>
      </div>
      <p style={{ color: "#5f7568" }}>{form.description}</p>

      {resumeCandidate && (
        <div className="pf-resume" data-testid="pf-resume-banner">
          <Clock size={16} />
          <div>
            <b>Resume where you left off?</b>
            <span> We saved your progress on this device{resumeCandidate.saved_at ? ` on ${formatSavedAt(resumeCandidate.saved_at)}` : ""}.</span>
          </div>
          <div className="pf-resume-actions">
            <button className="primary" onClick={resumeDraft} data-testid="pf-resume-yes">Resume</button>
            <button className="outline" onClick={discardDraft} data-testid="pf-resume-no">Start over</button>
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pf-steps" data-testid="pf-steps">
          <div className="pf-progress-track"><div className="pf-progress-fill" style={{ width: `${(page / totalPages) * 100}%` }} /></div>
          <div className="pf-steps-labels">
            {Array.from({ length: totalPages }, (_, i) => {
              const n = i + 1;
              const title = (form.page_titles || [])[i] || `Step ${n}`;
              const state = n < page ? "done" : n === page ? "current" : "pending";
              return (
                <span key={n} className={`pf-step ${state}`} data-testid={`pf-step-${n}`}>
                  <b>{n}</b>{title}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
        {page === 1 && (
          <>
            <label>Your name<input value={name} onChange={(e) => setName(e.target.value)} data-testid="pf-name" /></label>
            <label>Email (optional)<input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pf-email" /></label>
          </>
        )}
        {currentPageFields.map((f) => (
          <FieldRenderer key={f.field_id} field={f} value={values[f.field_id]} onChange={(v) => setVal(f.field_id, v)} />
        ))}
        {currentPageFields.length === 0 && page > 1 && (
          <p className="hint">All fields on this step are optional based on your earlier answers. Continue to the next step.</p>
        )}
        {err && <div style={{ color: "#b04a3f" }} data-testid="pf-error">{err}</div>}

        <div className="pf-actions">
          {draftStatus && <small className="pf-draft-status" data-testid="pf-draft-status"><Clock size={11} /> {draftStatus}</small>}
          {page > 1 && <button className="outline" onClick={goBack} data-testid="pf-back" disabled={busy}>← Back</button>}
          {!isLastPage && <button className="primary" onClick={goNext} data-testid="pf-next" disabled={busy}>Next →</button>}
          {isLastPage && <button className="primary" onClick={submit} disabled={busy || !name} data-testid="pf-submit" style={{ padding: "12px 20px", fontSize: 15 }}>{busy ? "Submitting…" : "Submit"}</button>}
        </div>
      </div>
    </PublicShell>
  );
}

function FieldRenderer({ field, value, onChange }) {
  const tid = `pf-${field.field_id}`;
  const label = <span>{field.label}{field.required ? " *" : ""}</span>;
  const help = field.help_text ? <small style={{ color: "#7a8a80", display: "block", marginTop: 2 }}>{field.help_text}</small> : null;
  const wrap = (input) => <label>{label}{input}{help}</label>;

  switch (field.type) {
    case "long_text":
      return wrap(<textarea rows={4} data-testid={tid} placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} />);
    case "number":
      return wrap(<input type="number" data-testid={tid} placeholder={field.placeholder} min={field.min_value ?? undefined} max={field.max_value ?? undefined} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />);
    case "date":
      return wrap(<input type="date" data-testid={tid} value={value || ""} onChange={(e) => onChange(e.target.value)} />);
    case "phone":
      return wrap(<input type="tel" data-testid={tid} placeholder={field.placeholder || "e.g. +91 98xxxxxxxx"} value={value || ""} onChange={(e) => onChange(e.target.value)} />);
    case "email":
      return wrap(<input type="email" data-testid={tid} placeholder={field.placeholder || "name@example.com"} value={value || ""} onChange={(e) => onChange(e.target.value)} />);
    case "single_select":
      return wrap(
        <select data-testid={tid} value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">— select —</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case "multi_select": {
      const arr = Array.isArray(value) ? value : [];
      const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
      return (
        <div>
          {label}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }} data-testid={tid}>
            {(field.options || []).map((o) => (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 400 }}>
                <input type="checkbox" data-testid={`${tid}-${o}`} checked={arr.includes(o)} onChange={() => toggle(o)} /> {o}
              </label>
            ))}
          </div>
          {help}
        </div>
      );
    }
    case "radio":
      return (
        <div>
          {label}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }} data-testid={tid}>
            {(field.options || []).map((o) => (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 400 }}>
                <input type="radio" name={field.field_id} data-testid={`${tid}-${o}`} checked={value === o} onChange={() => onChange(o)} /> {o}
              </label>
            ))}
          </div>
          {help}
        </div>
      );
    case "boolean":
      return (
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 500 }}>
          <input type="checkbox" data-testid={tid} checked={!!value} onChange={(e) => onChange(e.target.checked)} />
          {label}
          {help}
        </label>
      );
    case "rating": {
      const n = Number(value) || 0;
      return (
        <div>
          {label}
          <div style={{ display: "flex", gap: 6, marginTop: 6 }} data-testid={tid}>
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} type="button" onClick={() => onChange(s)} data-testid={`${tid}-${s}`}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: n >= s ? "#efb64e" : "#c8d3ce" }}>
                <Star size={26} fill={n >= s ? "#efb64e" : "none"} />
              </button>
            ))}
          </div>
          {help}
        </div>
      );
    }
    case "file":
    case "multi_file":
      return wrap(<FileInput field={field} onChange={onChange} />);
    default:
      return wrap(<input data-testid={tid} placeholder={field.placeholder} maxLength={field.max_length ?? undefined} value={value || ""} onChange={(e) => onChange(e.target.value)} />);
  }
}

function FileInput({ field, onChange }) {
  const [busy, setBusy] = useState(false);
  const [ids, setIds] = useState([]);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    const uploaded = [];
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      const r = await http.post("/uploads/form", fd, { headers: { "Content-Type": "multipart/form-data" } });
      uploaded.push(r.data.file_id);
    }
    const next = field.type === "multi_file" ? [...ids, ...uploaded] : uploaded.slice(0, 1);
    setIds(next); onChange(field.type === "multi_file" ? next : next[0]);
    setBusy(false);
  };
  return (
    <>
      <input type="file" multiple={field.type === "multi_file"} onChange={handle} disabled={busy} data-testid={`pf-file-${field.field_id}`} />
      {ids.length > 0 && <small style={{ color: "#0f766e" }}>{ids.length} file(s) attached</small>}
    </>
  );
}

function PublicShell({ children }) {
  return (
    <div className="public-shell" style={{ minHeight: "100vh", background: "#f0f6f3", padding: 24 }}>
      <div style={{ maxWidth: 640, margin: "0 auto", background: "white", padding: 32, borderRadius: 16, border: "1px solid #dbe7df", boxShadow: "0 8px 30px rgba(15,118,110,.08)" }}>
        {children}
      </div>
    </div>
  );
}

function formatSavedAt(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return ""; }
}
